<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const email = defineModel<string>('email', { default: '' });

const emit = defineEmits<{
	(e: 'social-login', provider: 'telegram' | 'google'): void;
}>();

const emailError = ref('');

const isValidEmail = (value: string): boolean => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const validateEmail = () => {
	if (!email.value.trim()) {
		emailError.value = '';
		return;
	}
	if (!isValidEmail(email.value.trim())) {
		emailError.value = t('accountEmail.invalid_email');
		return;
	}
	emailError.value = '';
};
</script>

<template>
	<div class="checkout-auth">
		<h3 class="checkout-auth__title">
			{{ t('payment.enter_email') }}
		</h3>

		<div
			class="checkout-auth__email-field"
			:class="{ 'checkout-auth__email-field_error': emailError }"
		>
			<input
				v-model="email"
				type="email"
				class="checkout-auth__email-input"
				:placeholder="t('login.login_field.placeholder')"
				@blur="validateEmail"
				@input="emailError = ''"
			/>
		</div>

		<div class="checkout-auth__social">
			<span class="checkout-auth__social-label">
				{{ t('login.login_with') }}
			</span>
			<div class="checkout-auth__social-icons">
				<img
					src="/images/telegram.svg"
					alt="Telegram"
					class="checkout-auth__social-icon"
					@click="emit('social-login', 'telegram')"
				/>
				<img
					src="/images/google.svg"
					alt="Google"
					class="checkout-auth__social-icon"
					@click="emit('social-login', 'google')"
				/>
			</div>
		</div>

		<div class="checkout-auth__trial">
			<img src="/images/verified-check.svg" alt="" class="checkout-auth__trial-icon" />
			<span class="checkout-auth__trial-text">{{ t('payment.trial_hint') }}</span>
		</div>
	</div>
</template>

<style lang="scss">
.checkout-auth {
	margin-bottom: 3.2rem;

	&__title {
		@include font-24-pro-bold;
		margin-bottom: 1.6rem;

		@include max-desktop {
			font-size: 2.1rem;
		}
	}

	&__email-field {
		height: 5.2rem;
		border: 1px solid rgba(0, 28, 51, 0.12);
		border-radius: 1.6rem;
		padding: 0 1.6rem;
		margin-bottom: 1.2rem;
		transition: border-color 0.2s ease;

		@include theme-dark {
			border-color: rgba(255, 255, 255, 0.12);
		}

		@include min-desktop {
			height: 6.2rem;
		}

		&_error {
			border-color: #ff2c58;
		}
	}

	&__email-input {
		width: 100%;
		height: 100%;
		border: none;
		background: none;
		outline: none;
		@include font-18-pro-medium;
		color: var(--color-text);

		&::placeholder {
			color: rgba(0, 28, 51, 0.5);

			@include theme-dark {
				color: rgba(255, 255, 255, 0.5);
			}
		}

		@include max-desktop {
			@include font-16-pro-regular;
		}
	}

	&__social {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 5.2rem;
		padding: 0 2.4rem;
		border-radius: 1.6rem;
		background: rgba(var(--color-active-rgb), 0.06);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		margin-bottom: 1.2rem;

		@include min-desktop {
			height: 6.2rem;
		}
	}

	&__social-label {
		@include font-16-pro-regular;
		opacity: 0.5;
	}

	&__social-icons {
		display: flex;
		align-items: center;
		gap: 2.4rem;
	}

	&__social-icon {
		width: 2.4rem;
		height: 2.4rem;
		cursor: pointer;
		transition: opacity 0.2s;

		&:hover { opacity: 0.8; }

		@include min-desktop {
			width: 3.2rem;
			height: 3.2rem;
		}
	}

	&__trial {
		display: flex;
		align-items: center;
		gap: 0.6rem;
	}

	&__trial-icon {
		width: 2rem;
		height: 2rem;
		flex: 0 0 auto;

		@include min-desktop {
			width: 2.4rem;
			height: 2.4rem;
		}
	}

	&__trial-text {
		@include font-regular;
		// Light: solid purple
		color: #792DFC;

		// Dark: gradient text
		@include theme-dark {
			background:
				linear-gradient(90deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)),
				linear-gradient(184deg, #fff 0%, #792DFC 31%, #C82DFC 58%, #fff 95%);
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			background-clip: text;
		}

		@include min-desktop {
			@include font-16-pro-regular;
		}
	}
}
</style>
